/**
 * Phase 2 SMB Modal Analytics Tracking
 *
 * Google Analytics (GTM) tracking for the NewCustomerSignupModalOTP component.
 * Tracks the full SMB signup flow including OTP verification, payment, and billing setup.
 *
 * Events tracked:
 * - Modal lifecycle (open/close)
 * - Scheduling consultation
 * - Phone/OTP verification
 * - User details submission
 * - Payment setup and completion
 * - Error tracking
 *
 * All events include:
 * - user_status (logged_in/returning/new)
 * - UTM parameters (if present in URL)
 * - Expert context (name, ID, rate)
 * - Duration tracking
 */
import {
  type UTMParams,
  type UserStatus,
  calculateDuration,
  captureUTMParams,
  getUserStatus,
} from '@/auth/utils/analyticsHelpers';
import { gtm } from '@/core/analytics';

export { calculateDuration, captureUTMParams, getUserStatus };
export type { UTMParams, UserStatus };

/**
 * Transport method for OTP (SMS or Email)
 */
type OTPTransport = 'sms' | 'email';

/**
 * Screen names in the Phase 2 flow
 */
export type ScreenName = 'scheduling' | 'phone' | 'otp' | 'details' | 'payment';

/**
 * Error types for tracking
 */
type ErrorType =
  | 'otp_invalid'
  | 'otp_expired'
  | 'payment_failed'
  | 'api_error'
  | 'network_error'
  | 'validation_error';

/**
 * Common event parameters
 */
interface BaseEventParams {
  user_status: UserStatus;
  timestamp: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate referral context from UTM parameters
 */
export function generateReferralContext(utmParams: UTMParams): string | null {
  if (!utmParams.utm_source && !utmParams.utm_campaign) {
    return null;
  }

  const parts: string[] = [];

  if (utmParams.utm_source) {
    parts.push(`via ${utmParams.utm_source}`);
  }

  if (utmParams.utm_campaign) {
    parts.push(`campaign: ${utmParams.utm_campaign}`);
  }

  return parts.length > 0 ? `Referred ${parts.join(', ')}` : null;
}

/**
 * Get base event parameters
 */
function getBaseParams(): BaseEventParams {
  return {
    user_status: getUserStatus(),
    timestamp: new Date().toISOString(),
  };
}

// ============================================================================
// MODAL LIFECYCLE EVENTS
// ============================================================================

/**
 * Track modal opened event
 */
export function trackSMBModalOpened(
  expertId: string,
  expertName: string,
  hourlyRate?: number
): void {
  const params = {
    event: 'smb_modal_opened',
    ...getBaseParams(),
    ...captureUTMParams(),
    expert_id: expertId,
    expert_name: expertName,
    hourly_rate: hourlyRate,
    url: window.location.href,
  };

  gtm('event', 'smb_modal_opened', params);
}

/**
 * Track modal closed event (without completing)
 */
export function trackSMBModalClosed(lastScreen: ScreenName, startTime: string | Date): void {
  const duration = calculateDuration(startTime);
  const completionPercentage = calculateCompletionPercentage(lastScreen);

  const params = {
    event: 'smb_modal_closed',
    ...getBaseParams(),
    last_screen: lastScreen,
    duration_on_modal: duration,
    completion_percentage: completionPercentage,
  };

  gtm('event', 'smb_modal_closed', params);
}

/**
 * Calculate completion percentage based on last screen reached
 */
function calculateCompletionPercentage(lastScreen: ScreenName): number {
  const screenOrder: ScreenName[] = ['scheduling', 'phone', 'otp', 'details', 'payment'];
  const index = screenOrder.indexOf(lastScreen);
  return Math.round(((index + 1) / screenOrder.length) * 100);
}

// ============================================================================
// SCHEDULING STEP
// ============================================================================

/**
 * Track scheduling step completion
 */
export function trackSMBSchedulingCompleted(duration: number, hasDescription: boolean): void {
  const utmParams = captureUTMParams();
  const referralContext = generateReferralContext(utmParams);

  const params = {
    event: 'smb_scheduling_completed',
    ...getBaseParams(),
    ...utmParams,
    duration,
    has_description: hasDescription,
    referral_context: referralContext,
  };

  gtm('event', 'smb_scheduling_completed', params);
}

// ============================================================================
// PHONE/OTP STEPS
// ============================================================================

/**
 * Track phone number submission
 */
export function trackSMBPhoneSubmitted(
  countryCode: string,
  isReturningUser: boolean,
  phonePrefilled: boolean
): void {
  const params = {
    event: 'smb_phone_submitted',
    ...getBaseParams(),
    country_code: countryCode,
    is_returning_user: isReturningUser,
    phone_prefilled: phonePrefilled,
  };

  gtm('event', 'smb_phone_submitted', params);
}

/**
 * Track OTP code sent
 */
export function trackSMBOTPSent(
  transport: OTPTransport,
  maskedAddress: string,
  isResend: boolean
): void {
  const params = {
    event: 'smb_otp_sent',
    ...getBaseParams(),
    transport,
    masked_address: maskedAddress,
    is_resend: isResend,
  };

  gtm('event', 'smb_otp_sent', params);
}

/**
 * Track OTP verification success
 */
export function trackSMBOTPVerified(
  isReturningUser: boolean,
  userId: string,
  verificationDuration: number,
  verificationAttempts: number
): void {
  const params = {
    event: 'smb_otp_verified',
    ...getBaseParams(),
    is_returning_user: isReturningUser,
    user_id: userId,
    verification_duration: verificationDuration,
    verification_attempts: verificationAttempts,
  };

  gtm('event', 'smb_otp_verified', params);
}

// ============================================================================
// DETAILS STEP
// ============================================================================

/**
 * Track details screen viewed
 */
export function trackSMBDetailsViewed(
  isPartialForm: boolean,
  missingFields: string[],
  expertId: string,
  expertName: string,
  userId: string
): void {
  const params = {
    event: 'smb_details_viewed',
    ...getBaseParams(),
    is_partial_form: isPartialForm,
    missing_fields: missingFields,
    expert_id: expertId,
    expert_name: expertName,
    user_id: userId,
  };

  gtm('event', 'smb_details_viewed', params);
}

/**
 * Track user details form submission
 */
export function trackSMBDetailsSubmitted(
  isExistingUser: boolean,
  hasCompany: boolean,
  emailDomain: string,
  isPartialForm: boolean
): void {
  const params = {
    event: 'smb_details_submitted',
    ...getBaseParams(),
    ...captureUTMParams(),
    is_existing_user: isExistingUser,
    has_company: hasCompany,
    email_domain: emailDomain,
    is_partial_form: isPartialForm,
  };

  gtm('event', 'smb_details_submitted', params);
}

// ============================================================================
// PAYMENT STEPS
// ============================================================================

/**
 * Track payment step reached
 */
export function trackSMBPaymentStarted(
  duration: number,
  isReturningUser: boolean,
  userId: string,
  expertId: string
): void {
  const params = {
    event: 'smb_payment_started',
    ...getBaseParams(),
    duration,
    is_returning_user: isReturningUser,
    user_id: userId,
    expert_id: expertId,
  };

  gtm('event', 'smb_payment_started', params);
}

/**
 * Track payment method added successfully
 */
export function trackSMBPaymentMethodAdded(
  cardBrand: string,
  userId: string,
  expertId: string,
  paymentDuration: number
): void {
  const params = {
    event: 'smb_payment_method_added',
    ...getBaseParams(),
    card_brand: cardBrand,
    user_id: userId,
    expert_id: expertId,
    payment_duration: paymentDuration,
  };

  gtm('event', 'smb_payment_method_added', params);
}

/**
 * Track signup completion (billing account created)
 */
export function trackSMBSignupComplete(
  duration: number,
  isReturningUser: boolean,
  expertId: string,
  userId: string,
  billingAccountId: string
): void {
  const params = {
    event: 'smb_signup_complete',
    ...getBaseParams(),
    ...captureUTMParams(),
    duration,
    is_returning_user: isReturningUser,
    expert_id: expertId,
    user_id: userId,
    billing_account_id: billingAccountId,
  };

  gtm('event', 'smb_signup_complete', params);
}

// ============================================================================
// ERROR TRACKING
// ============================================================================

/**
 * Track error in the flow
 */
export function trackSMBError(
  errorType: ErrorType,
  screen: ScreenName,
  errorMessage: string,
  errorCode?: string
): void {
  const params = {
    event: 'smb_error',
    ...getBaseParams(),
    error_type: errorType,
    screen,
    error_message: errorMessage,
    ...(errorCode && { error_code: errorCode }),
  };

  gtm('event', 'smb_error', params);
}
